import React, { useState, useEffect } from 'react';
import { X, AlertCircle, Sparkles, TrendingUp, Heart, Moon } from 'lucide-react';

const PredictiveNotifications = () => {
  const [notifications, setNotifications] = useState([]);
  const [dismissed, setDismissed] = useState([]);

  useEffect(() => {
    // Generate dynamic notifications based on time/chart
    const generateNotifications = () => {
      const hour = new Date().getHours();
      const allNotifications = [];

      // Morning notifications
      if (hour >= 6 && hour < 12) {
        allNotifications.push({
          id: 'morning-energy',
          type: 'energy',
          icon: TrendingUp,
          title: 'High Energy Window',
          message: 'Your vitality peaks between 8-10 AM. Best time for important tasks.',
          color: '#10b981',
          priority: 'high',
        });
      }

      // Afternoon notifications
      if (hour >= 12 && hour < 17) {
        allNotifications.push({
          id: 'afternoon-focus',
          type: 'mental',
          icon: Sparkles,
          title: 'Mental Clarity Peak',
          message: 'Mercury is well-placed. Good time for learning and communication.',
          color: '#6366f1',
          priority: 'medium',
        });
      }

      // Evening notifications
      if (hour >= 17 && hour < 21) {
        allNotifications.push({
          id: 'evening-caution',
          type: 'caution',
          icon: AlertCircle,
          title: 'Avoid Overthinking',
          message: 'Moon transit suggests emotional sensitivity. Practice calm.',
          color: '#f59e0b',
          priority: 'medium',
        });
      }

      // Night notifications
      if (hour >= 21 || hour < 6) {
        allNotifications.push({
          id: 'night-rest',
          type: 'health',
          icon: Moon,
          title: 'Optimal Sleep Window',
          message: 'Your chart indicates best sleep quality before midnight.',
          color: '#8b5cf6',
          priority: 'high',
        });
      }

      // Always-on notifications
      allNotifications.push({
        id: 'health-reminder',
        type: 'health',
        icon: Heart,
        title: 'Health Focus Today',
        message: 'Mars in 6th house. Avoid heavy lifting, focus on flexibility.',
        color: '#ef4444',
        priority: 'high',
      });

      // Filter out dismissed notifications
      return allNotifications.filter(n => !dismissed.includes(n.id));
    };

    setNotifications(generateNotifications());
  }, [dismissed]);

  const dismissNotification = (id) => {
    setDismissed([...dismissed, id]);
  };

  if (notifications.length === 0) return null;

  return (
    <div className="space-y-3">
      {notifications.map((notif) => {
        const Icon = notif.icon;
        return (
          <div
            key={notif.id}
            className="relative bg-white rounded-card p-4 shadow-card hover:shadow-card-hover transition-all"
            style={{
              borderLeft: `4px solid ${notif.color}`,
            }}
          >
            {/* Dismiss button */}
            <button
              onClick={() => dismissNotification(notif.id)}
              className="absolute top-2 right-2 p-1 hover:bg-stone-100 rounded-full transition-colors"
            >
              <X className="w-4 h-4 text-stone-400" />
            </button>

            {/* Content */}
            <div className="flex items-start gap-3 pr-6">
              <div
                className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: `${notif.color}20` }}
              >
                <Icon className="w-5 h-5" style={{ color: notif.color }} />
              </div>
              <div className="flex-1">
                <h4 className="font-serif text-sm font-medium text-stone-900 mb-1">
                  {notif.title}
                </h4>
                <p className="text-xs text-stone-600 leading-relaxed">
                  {notif.message}
                </p>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default PredictiveNotifications;